// src/AppointmentTypesSettings.js
import React, { useState, useEffect } from 'react';
import TitleBar from './components/ui/TitleBar';
import NotificationOverlayFrame from './components/NotificationOverlayFrame';
import useTimedUnsavedWarning from './useTimedUnsavedWarning';

import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
const API_BASE = getApiBase();

// ── Mobile helper ──────────────────────────────────────────────────────────
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const compareVisibleNames = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, {
  sensitivity: 'base',
  numeric: true
});

const PREDEFINED_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e',
  '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1',
  '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e', '#64748b'
];

const DURATION_OPTIONS = [
  { value: 15, label: '15 MINUTES' },
  { value: 30, label: '30 MINUTES' },
  { value: 45, label: '45 MINUTES' },
  { value: 60, label: '1 HOUR' },
  { value: 90, label: '1.5 HOURS' },
  { value: 120, label: '2 HOURS' },
  { value: 150, label: '2.5 HOURS' },
  { value: 180, label: '3 HOURS' },
  { value: 240, label: '4 HOURS' },
  { value: 300, label: '5 HOURS' },
  { value: 360, label: '6 HOURS' },
  { value: 480, label: '8 HOURS' }
];

const AppointmentTypesSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [savedTypeIds, setSavedTypeIds] = useState([]);
  const { unsavedWarningVisible, showUnsavedWarning, clearUnsavedWarning } = useTimedUnsavedWarning();
  const [showAddType, setShowAddType] = useState(false);
  const [editingType, setEditingType] = useState(null);

  const sortedAppointmentTypes = [...appointmentTypes].sort((a, b) => compareVisibleNames(a.name, b.name));
  const appointmentTypeGroups = [
    { label: 'INTERNAL', types: sortedAppointmentTypes.filter(type => (type.visibility || 'internal') !== 'public') },
    { label: 'PUBLIC', types: sortedAppointmentTypes.filter(type => type.visibility === 'public') }
  ];

  const [typeForm, setTypeForm] = useState({
    name: '',
    color: '#3b82f6',
    duration: 60,
    description: '',
    visibility: 'internal'
  });

  // ── Responsive listener ────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile());
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load settings ──────────────────────────────────────────────────────────
  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/appointment-types`, {
        headers: { ...getTenantHeaders() }
      });
      if (res.ok) {
        const data = await res.json();
        const types = Array.isArray(data) ? data : [];
        setAppointmentTypes(types.map(type => ({ ...type, isNew: false })));
        setSavedTypeIds(types.map(type => String(type.id)));
        clearUnsavedWarning();
      }
    } catch (error) {
      console.error('Failed to load appointment types settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Save settings ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const retainedIds = new Set(
        appointmentTypes
          .filter(type => !type.isNew)
          .map(type => String(type.id))
      );
      const deletedIds = savedTypeIds.filter(id => !retainedIds.has(id));

      for (const type of appointmentTypes) {
        const res = await fetch(
          type.isNew
            ? `${API_BASE}/api/settings/appointment-types`
            : `${API_BASE}/api/settings/appointment-types/${type.id}`,
          {
            method: type.isNew ? 'POST' : 'PUT',
            headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
            body: JSON.stringify({
              name: type.name,
              color: type.color,
              duration: type.duration,
              description: type.description,
              visibility: type.visibility || 'internal',
              isActive: true
            })
          }
        );

        if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);

        if (type.isNew) {
          const data = await res.json();
          if (!data.id) throw new Error('Save failed: missing appointment type ID');

          setAppointmentTypes(prev => prev.map(item =>
            item.id === type.id
              ? { ...item, id: data.id, isNew: false }
              : item
          ));
        }
      }

      for (const id of deletedIds) {
        const res = await fetch(`${API_BASE}/api/settings/appointment-types/${id}`, {
          method: 'DELETE',
          headers: { ...getTenantHeaders() }
        });
        if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);
      }

      clearUnsavedWarning();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'APPOINTMENT TYPES SAVED!' }
      }));
      await loadSettings();
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save settings: ' + error.message);
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const addType = () => {
    const name = typeForm.name.trim();
    if (!name) return alert('Enter appointment type name');

    const newType = {
      id: Date.now(),
      isNew: true,
      name,
      color: typeForm.color,
      duration: typeForm.duration,
      description: typeForm.description.trim(),
      visibility: typeForm.visibility || 'internal'
    };

    setAppointmentTypes(prev => [...prev, newType]);
    showUnsavedWarning();
    setTypeForm({ name: '', color: '#3b82f6', duration: 60, description: '', visibility: 'internal' });
    setShowAddType(false);
  };

  const startEditType = (type) => {
    setEditingType(type.id);
    setTypeForm({
      name: type.name,
      color: type.color,
      duration: type.duration,
      description: type.description || '',
      visibility: type.visibility || 'internal'
    });
  };

  const saveEditType = () => {
    const name = typeForm.name.trim();
    if (!name) return alert('Enter appointment type name');

    setAppointmentTypes(prev => prev.map(t =>
      t.id === editingType
        ? { ...t, name, color: typeForm.color, duration: typeForm.duration, description: typeForm.description.trim(), visibility: typeForm.visibility || 'internal' }
        : t
    ));
    showUnsavedWarning();

    setEditingType(null);
    setTypeForm({ name: '', color: '#3b82f6', duration: 60, description: '', visibility: 'internal' });
  };

  const deleteType = (id) => {
    if (!window.confirm('DELETE THIS APPOINTMENT TYPE?')) return;
    setAppointmentTypes(prev => prev.filter(t => t.id !== id));
    showUnsavedWarning();
  };

  const getDurationLabel = (minutes) => {
    const option = DURATION_OPTIONS.find(d => d.value === minutes);
    return option ? option.label : `${minutes} MIN`;
  };

  const styles = {
    page: {
      padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'),
      backgroundColor: '#E8E2DA',
      minHeight: '100vh'
    },
    contentWrapper: {
      maxWidth: "1200px", padding: isPhone ? '0' : "0 12px",
      margin: '0 auto'
    },
    panel: {
      backgroundColor: '#E7B888',
      border: isMobile ? '6px solid black' : '10px solid black',
      padding: isPhone ? '15px 5px' : (isMobile ? '15px' : '40px'),
      marginBottom: '18px'
    },
    label: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '13px',
      marginBottom: '6px',
      display: 'block'
    },
    input: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box'
    },
    select: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box',
      backgroundColor: 'white'
    },
    textarea: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box',
      minHeight: '100px',
      fontFamily: 'inherit'
    },
    row1: {
      marginBottom: '12px'
    },
    addBtn: {
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px 20px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '14px',
      marginBottom: '12px'
    },
    card: {
      border: '4px solid black',
      padding: '14px',
      marginBottom: '12px',
      backgroundColor: 'white'
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '10px',
      flexWrap: 'wrap'
    },
    cardInfo: {
      flex: 1,
      minWidth: '200px'
    },
    cardName: {
      fontWeight: 900,
      fontSize: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    colorDot: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      border: '2px solid black'
    },
    cardMeta: {
      fontWeight: 900,
      fontSize: '14px',
      marginTop: '4px',
      color: '#666'
    },
    cardActions: {
      display: 'flex',
      gap: '8px'
    },
    editBtn: {
      background: 'white',
      color: 'black',
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    deleteBtn: {
      background: '#ff4444',
      color: 'white',
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    saveBtn: {
      background: 'black',
      color: '#FFA500',
      border: '5px solid #E8761D',
      padding: '16px 24px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '18px',
      width: isMobile ? '100%' : 'auto'
    },
    modal: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 5000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      border: '8px solid black',
      padding: '24px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    modalTitle: {
      fontWeight: 900,
      fontSize: '22px',
      marginBottom: '16px',
      textTransform: 'uppercase'
    },
    modalBtnRow: {
      display: 'flex',
      gap: '10px',
      marginTop: '16px'
    },
    confirmBtn: {
      flex: 1,
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    cancelBtn: {
      flex: 1,
      background: 'white',
      color: 'black',
      border: '4px solid black',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    colorGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))',
      gap: '8px',
      marginTop: '10px'
    },
    colorOption: {
      width: '50px',
      height: '50px',
      border: '3px solid black',
      cursor: 'pointer',
      borderRadius: '4px'
    },
    colorOptionActive: {
      border: '4px solid #2D5F3F',
      transform: 'scale(1.1)'
    },
    note: {
      fontWeight: 900,
      fontSize: '13px',
      padding: '12px',
      border: '3px solid black',
      backgroundColor: '#ffffcc',
      marginBottom: '12px'
    },
    loadingBox: {
      padding: '30px',
      textAlign: 'center',
      fontWeight: 900,
      fontSize: '16px'
    }
  };

  // ── Visibility badge shown on each card ────────────────────────────────────
  const visibilityBadgeStyle = (vis) => ({
    display: 'inline-block',
    marginTop: '6px',
    padding: '3px 10px',
    fontWeight: 900,
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    border: '2px solid black',
    backgroundColor: vis === 'public' ? '#E8761D' : '#e2e8f0',
    color: vis === 'public' ? '#000' : '#444'
  });

  // ── Visibility dropdown (reused in Add and Edit modals) ────────────────────
  const VisibilityRow = () => (
    <div style={styles.row1}>
      <label style={styles.label}>VISIBILITY</label>
      <select
        style={styles.select}
        value={typeForm.visibility}
        onChange={e => setTypeForm({ ...typeForm, visibility: e.target.value })}
      >
        <option value="internal">🔒 INTERNAL — Only you and your team see this</option>
        <option value="public">🌐 PUBLIC — Customers can book this online</option>
      </select>
    </div>
  );

  return (
    <div style={styles.page}>
      <style>{`
        .jf-appt-pink:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
        .jf-appt-green:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
      `}</style>
      <div style={styles.contentWrapper}>
        <TitleBar
          icon="📅"
          title="APPOINTMENT TYPES"
          accent="#E8761D"
          right={
            <button
              className="jf-appt-pink"
              onClick={() => navigate('/settings')}
              style={{
                background: 'black',
                color: '#FFA500',
                border: '5px solid #E8761D',
                padding: '14px',
                fontWeight: 900,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING APPOINTMENT TYPES...</div>
          ) : (
            <>
              <div style={styles.note}>
                Create appointment types that will be color-coded on your calendar. Set visibility to PUBLIC to allow customers to book online.
              </div>

              <button className="jf-appt-pink" style={styles.addBtn} onClick={() => setShowAddType(true)}>
                + ADD APPOINTMENT TYPE
              </button>

              {appointmentTypeGroups.map(group => (
                <div key={group.label}>
                  <h2 style={{ fontWeight: 900, margin: '16px 0 10px', textTransform: 'uppercase' }}>{group.label}</h2>
                  {group.types.length === 0 ? (
                    <div style={{ ...styles.note, backgroundColor: 'white' }}>
                      No {group.label.toLowerCase()} appointment types added yet.
                    </div>
                  ) : (
                    group.types.map(type => (
                      <div key={type.id} style={styles.card}>
                        <div style={styles.cardHeader}>
                          <div style={styles.cardInfo}>
                            <div style={styles.cardName}>
                              <div style={{ ...styles.colorDot, backgroundColor: type.color }} />
                              {type.name}
                            </div>
                            <div style={styles.cardMeta}>DURATION: {getDurationLabel(type.duration)}</div>
                            <div>
                              <span style={visibilityBadgeStyle(type.visibility || 'internal')}>
                                {(type.visibility || 'internal') === 'public' ? '🌐 PUBLIC' : '🔒 INTERNAL'}
                              </span>
                            </div>
                            {type.description && (
                              <div style={{ ...styles.cardMeta, marginTop: '8px', fontStyle: 'italic' }}>
                                {type.description}
                              </div>
                            )}
                          </div>
                          <div style={styles.cardActions}>
                            <button className="jf-appt-green" style={styles.editBtn} onClick={() => startEditType(type)}>
                              EDIT
                            </button>
                            <button className="jf-appt-green" style={styles.deleteBtn} onClick={() => deleteType(type.id)}>
                              DELETE
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ))}

              <div style={{ marginTop: '24px' }}>
                {unsavedWarningVisible && (
                  <NotificationOverlayFrame accentColor="#FFC107" role="status" ariaLive="polite">
                    <div style={{ fontWeight: 900, fontSize: 'clamp(18px, 4.8vw, 22px)', lineHeight: 1.2 }}>
                      APPOINTMENT TYPE CHANGES NOT SAVED YET
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 'clamp(15px, 4vw, 18px)', lineHeight: 1.35, marginTop: '6px' }}>
                      Select SAVE ALL CHANGES to keep your changes.
                    </div>
                  </NotificationOverlayFrame>
                )}
                <button
                  className="jf-appt-pink"
                  style={styles.saveBtn}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'SAVING...' : 'SAVE ALL CHANGES'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── ADD MODAL ── */}
        {showAddType && (
          <div style={styles.modal} onClick={() => setShowAddType(false)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>ADD APPOINTMENT TYPE</h2>

              <div style={styles.row1}>
                <label style={styles.label}>APPOINTMENT TYPE NAME *</label>
                <input
                  style={styles.input}
                  value={typeForm.name}
                  onChange={e => setTypeForm({ ...typeForm, name: e.target.value })}
                  placeholder="e.g., Oil Change, Installation"
                />
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>DURATION *</label>
                <select
                  style={styles.select}
                  value={typeForm.duration}
                  onChange={e => setTypeForm({ ...typeForm, duration: Number(e.target.value) })}
                >
                  {DURATION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <VisibilityRow />

              <div style={styles.row1}>
                <label style={styles.label}>CALENDAR COLOR *</label>
                <div style={styles.colorGrid}>
                  {PREDEFINED_COLORS.map(color => (
                    <div
                      key={color}
                      style={{
                        ...styles.colorOption,
                        backgroundColor: color,
                        ...(typeForm.color === color ? styles.colorOptionActive : {})
                      }}
                      onClick={() => setTypeForm({ ...typeForm, color })}
                    />
                  ))}
                </div>
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>DESCRIPTION / NOTES (OPTIONAL)</label>
                <textarea
                  style={styles.textarea}
                  value={typeForm.description}
                  onChange={e => setTypeForm({ ...typeForm, description: e.target.value })}
                  placeholder="Job notes or special instructions..."
                />
              </div>

              <div style={styles.modalBtnRow}>
                <button className="jf-appt-pink" style={styles.confirmBtn} onClick={addType}>ADD</button>
                <button className="jf-appt-green" style={styles.cancelBtn} onClick={() => {
                  setShowAddType(false);
                  setTypeForm({ name: '', color: '#3b82f6', duration: 60, description: '', visibility: 'internal' });
                }}>CANCEL</button>
              </div>
            </div>
          </div>
        )}

        {/* ── EDIT MODAL ── */}
        {editingType && (
          <div style={styles.modal} onClick={() => setEditingType(null)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>EDIT APPOINTMENT TYPE</h2>

              <div style={styles.row1}>
                <label style={styles.label}>APPOINTMENT TYPE NAME *</label>
                <input
                  style={styles.input}
                  value={typeForm.name}
                  onChange={e => setTypeForm({ ...typeForm, name: e.target.value })}
                />
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>DURATION *</label>
                <select
                  style={styles.select}
                  value={typeForm.duration}
                  onChange={e => setTypeForm({ ...typeForm, duration: Number(e.target.value) })}
                >
                  {DURATION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <VisibilityRow />

              <div style={styles.row1}>
                <label style={styles.label}>CALENDAR COLOR *</label>
                <div style={styles.colorGrid}>
                  {PREDEFINED_COLORS.map(color => (
                    <div
                      key={color}
                      style={{
                        ...styles.colorOption,
                        backgroundColor: color,
                        ...(typeForm.color === color ? styles.colorOptionActive : {})
                      }}
                      onClick={() => setTypeForm({ ...typeForm, color })}
                    />
                  ))}
                </div>
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>DESCRIPTION / NOTES (OPTIONAL)</label>
                <textarea
                  style={styles.textarea}
                  value={typeForm.description}
                  onChange={e => setTypeForm({ ...typeForm, description: e.target.value })}
                />
              </div>

              <div style={styles.modalBtnRow}>
                <button className="jf-appt-pink" style={styles.confirmBtn} onClick={saveEditType}>SAVE</button>
                <button className="jf-appt-green" style={styles.cancelBtn} onClick={() => {
                  setEditingType(null);
                  setTypeForm({ name: '', color: '#3b82f6', duration: 60, description: '', visibility: 'internal' });
                }}>CANCEL</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentTypesSettings;
